import { breakpointNumbers } from '@ui/constants/design-tokens/breakpoints';
import { useEffect, useMemo, useState } from 'react';

type BreakpointNumbers = typeof breakpointNumbers;

type ContainerBreakpointCheckerFn = (
  size: {
    width: number;
    height: number;
  },
  breakpointNumbers: BreakpointNumbers,
) => boolean;

export function useContainerBreakPoint({
  containerQuerySelector,
  breakpointChecker,
}: {
  /**
   * not using ref since usually container children use this hook
   */
  containerQuerySelector: string;
  breakpointChecker: ContainerBreakpointCheckerFn;
}) {
  const [value, setValue] = useState<boolean | null>(null);
  const [memoizedBreakpointChecker] = useState(() => breakpointChecker);
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0]) {
        const { inlineSize: width, blockSize: height } =
          entries[0].contentBoxSize[0];
        setValue(
          memoizedBreakpointChecker({ width, height }, breakpointNumbers),
        );
      }
    });
    const elm = document.querySelector(containerQuerySelector);

    if (elm) {
      resizeObserver.observe(elm);
    }

    return () => {
      if (elm) {
        resizeObserver.unobserve(elm);
      }
    };
  }, [memoizedBreakpointChecker, containerQuerySelector]);

  return useMemo(
    () => ({
      value,
      isPending: value === null, // not mounted yet
    }),
    [value],
  );
}
